import { Away, fullNameKey } from "odyssey-shared";
import { say } from "../../words.ts";
import { scopeTipPlace } from "./scope-icon.tsx";
import type { PlayerName } from "../player-names.ts";

/**
 * The chat name's hover card (ADR-068's amendment of 2026-10-27, P1.9-T87): the facts the page holds about a character
 * it draws, set as the lines under the full name. A character the page does not show gets the full name alone.
 */

/**
 * What the page's view holds about a character that the card lists: its level and its away state.
 */
export interface CardFacts {
  readonly level: number;
  readonly away: number;
}

/**
 * The card's lines: the full name, then the level and the status when the page holds facts.
 *
 * @param full The character's full name.
 * @param facts The facts, or null for a character the page does not show.
 * @returns The lines, top to bottom.
 * @example
 * cardLines("Mira Thornwood", { level: 12, away: Away.Active });
 * // ["Mira Thornwood", "Lv 12", "Online"]
 */
export function cardLines(full: string, facts: CardFacts | null): readonly string[] {
  if (facts === null) {
    return [full];
  }
  const status = facts.away === Away.Afk ? say("chat.card.afk") : say("chat.card.online");
  return [full, say("chat.card.level", { level: facts.level }), status];
}

/**
 * The facts of the character a full name names, only when the page draws it and its world UI is not hidden: a character
 * behind a prop or under a roof the viewer is not under is not confirmed as near (the stepper's name list follows the
 * same rule).
 *
 * @param full The full name the card was opened on.
 * @param names The names of the players the view holds.
 * @param drawn The characters the page draws by handle, each with whether its world UI is hidden.
 * @param read Reads a handle's facts from the view; null for one the view no longer holds.
 * @returns The facts, or null.
 */
export function cardFactsOf(full: string, names: readonly PlayerName[], drawn: ReadonlyMap<number, { readonly hidden: boolean }>, read: (handle: number) => CardFacts | null): CardFacts | null {
  const key = full.toLowerCase();
  const found = names.find((name) => fullNameKey(name.firstName, name.familyName) === key);
  return found === undefined || drawn.get(found.handle)?.hidden !== false ? null : read(found.handle);
}

/**
 * The logical height of a card of some lines: the body line height of 10 per line and the frame and padding round them.
 *
 * @param lines The number of lines.
 * @returns The height in logical px.
 */
export function cardHeight(lines: number): number {
  return lines * 10 + 10;
}

/**
 * Where a card stands over its name, in logical px of the panel body: above the name as a tooltip stands when the log's
 * rows above the name hold the whole card, else below the name, so a card never covers the tab strip.
 *
 * @param name The name's client rectangle.
 * @param log The log's client rectangle, whose top is the tab strip's foot.
 * @param box The positioned body's client rectangle.
 * @param px The device pixels a logical pixel takes.
 * @param lines The number of lines the card has.
 * @returns `left` and either `bottom` (above the name) or `top` (below it), whole logical px.
 * @example
 * cardPlace({ left: 14, top: 62, bottom: 72 }, { top: 60 }, { left: 10, top: 20, bottom: 100 }, 1, 3);
 * // { left: 4, top: 53 }
 */
export function cardPlace(name: { readonly left: number; readonly top: number; readonly bottom: number }, log: { readonly top: number }, box: { readonly left: number; readonly top: number; readonly bottom: number }, px: number, lines: number): { readonly left: number; readonly bottom?: number; readonly top?: number } {
  const above = scopeTipPlace(name, box, px);
  if ((name.top - log.top) / px >= cardHeight(lines) + 1) {
    return above;
  }
  return { left: above.left, top: Math.round((name.bottom - box.top) / px) + 1 };
}
